import { FieldValues, FormProvider, SubmitHandler } from "react-hook-form";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { EditableSettingRow } from "./EditableSettingRow";
import { ReadonlySettingRow } from "./ReadonlySettingRow";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { useForm } from "../../../../../hooks/useForm";
import React, { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { PricingRule } from "../../PricingSettings";

type PricingSettingRowProps = {
  rule: PricingRule;
  isEditing: boolean;
  onEditStart: () => void;
  onEditStop: (_savedData?: PricingRule) => void;
  showEdit: boolean;
  method: "post" | "put" | "patch" | "delete";
  url: string;
  customHeader?: React.ReactNode;
  deletable?: boolean;
};

export const PricingSettingRow = (props: PricingSettingRowProps) => {
  const {
    rule,
    isEditing,
    onEditStart,
    onEditStop,
    showEdit,
    url,
    customHeader,
    method,
    deletable = true,
  } = props;

  const { translate } = useTranslation("pages.stores.pricingSettings");
  const theme = useTheme();

  const { isLoading, mutate } = useCustomMutation();
  const invalidate = useInvalidate();

  const methods = useForm<PricingRule>({
    defaultValues: rule,
    mode: "onChange",
  });

  const { handleSubmit, reset, getValues } = methods;

  // When the rule is changed from sole seller setting row, reset the form
  // Otherwise, when this row is saved, sole seller settings will be updated
  // with old values
  useEffect(() => {
    if (getValues() != rule) reset(rule);
  }, [rule, reset, getValues]);

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    mutate(
      {
        url: url,
        method: method,
        values: data,
        successNotification: () => {
          return {
            message: translate("successUpdate"),
            type: "success",
          };
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "all",
            invalidates: ["all"],
          });

          handleClose(data);
        },
      },
    );
  };

  const handleClose = (data?: FieldValues) => {
    reset(data ?? rule);
    onEditStop(data ? (data as PricingRule) : undefined);
  };

  return (
    <FormProvider {...methods}>
      {isEditing ? (
        <EditableSettingRow
          isLoading={isLoading}
          onClose={() => handleClose()}
          onSave={handleSubmit(onSubmit)}
          customHeader={customHeader}
        />
      ) : (
        <ReadonlySettingRow
          rule={rule}
          onEditStart={onEditStart}
          showEdit={showEdit}
          customHeader={customHeader}
          deletable={deletable}
          backgroundColor={
            deletable ? "transparent" : theme.palette.highlightedRow.main
          }
          rulesUrl={url}
        />
      )}
    </FormProvider>
  );
};
